import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Clock4,
  Download,
  Grid3x3,
  Search,
  Settings,
  Target,
  TrendingUp,
} from "lucide-react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

type Props = {
  isSubscribed: boolean;
};

const Landing: FC<Props> = ({ isSubscribed }) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        height: "calc(100dvh - var(--height-header))",
      }}
      className="bg-[#F7F8F9] p-[40px] 2xl:px-[120px] 3xl:px-[260px] overflow-y-auto overflow-x-hidden flex flex-col"
    >
      {/* First Part */}
      <div className="flex gap-[32px] justify-between">
        <div className="flex flex-col gap-[24px]">
          <div className="h-[24px] flex items-center justify-center rounded-[8px] px-[8px] bg-[#E7F9FF] w-[fit-content]">
            <Text
              fontSize={14}
              fontWeight={500}
              color="#206A83"
              className="uppercase"
            >
              {t("grid_builder")}
            </Text>
          </div>
          <div className="flex flex-col">
            <Text fontSize={28} fontWeight={700} color="#44546F">
              {t("grid_builder_landing_title")}
            </Text>
            <Text fontSize={28} fontWeight={700} color="#155DFC">
              {t("grid_builder_landing_subtitle")}
            </Text>
            <Text fontWeight={400} fontSize={16}>
              {t("grid_builder_landing_description")}
            </Text>
          </div>
          <Link to={isSubscribed ? "/grid-builder/create" : "/upgrade"}>
            <Button className="w-[fit-content]">
              {isSubscribed
                ? t("create_view")
                : t("grid_builder_landing_action")}
            </Button>
          </Link>
        </div>
        <img src="/svg/grid-builder-landing.svg" />
      </div>

      {/* Second Part */}
      <div className="grid grid-cols-2 my-[40px] gap-x-[32px] gap-y-[24px]">
        <div className="flex gap-[16px]">
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Grid3x3 height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[6px]">
            <Text color="#44546F" fontSize={16} fontWeight={600}>
              {t("grid_builder_landing_1_title")}
            </Text>
            <Text fontSize={14} fontWeight={400} color="#687383">
              {t("grid_builder_landing_1_description")}
            </Text>
          </div>
        </div>

        <div className="flex gap-[16px]">
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Search height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[6px]">
            <Text color="#44546F" fontSize={16} fontWeight={600}>
              {t("grid_builder_landing_2_title")}
            </Text>
            <Text fontSize={14} fontWeight={400} color="#687383">
              {t("grid_builder_landing_2_description")}
            </Text>
          </div>
        </div>

        <div className="flex gap-[16px]">
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Download height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[6px]">
            <Text color="#44546F" fontSize={16} fontWeight={600}>
              {t("grid_builder_landing_3_title")}
            </Text>
            <Text fontSize={14} fontWeight={400} color="#687383">
              {t("grid_builder_landing_3_description")}
            </Text>
          </div>
        </div>

        <div className="flex gap-[16px]">
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Settings height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[6px]">
            <Text color="#44546F" fontSize={16} fontWeight={600}>
              {t("grid_builder_landing_4_title")}
            </Text>
            <Text fontSize={14} fontWeight={400} color="#687383">
              {t("grid_builder_landing_4_description")}
            </Text>
          </div>
        </div>
      </div>

      {/* Thrid Part */}
      <div className="grid grid-cols-3 gap-[12px]">
        <div
          className="bg-[white] rounded-[12px] p-[24px] gap-[22px] flex"
          style={{ border: "1px solid #F4F4F4" }}
        >
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Clock4 height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[4px]">
            <Text fontSize={14} fontWeight={600} color="#101828">
              {t("grid_builder_landing_5_title")}
            </Text>
            <Text color="#4A5565" fontSize={12} fontWeight={400}>
              {t("grid_builder_landing_5_description")}
            </Text>
          </div>
        </div>

        <div
          className="bg-[white] rounded-[12px] p-[24px] gap-[22px] flex"
          style={{ border: "1px solid #F4F4F4" }}
        >
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <Target height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[4px]">
            <Text fontSize={14} fontWeight={600} color="#101828">
              {t("grid_builder_landing_6_title")}
            </Text>
            <Text color="#4A5565" fontSize={12} fontWeight={400}>
              {t("grid_builder_landing_6_description")}
            </Text>
          </div>
        </div>

        <div
          className="bg-[white] rounded-[12px] p-[24px] gap-[22px] flex"
          style={{ border: "1px solid #F4F4F4" }}
        >
          <div className="flex items-center justify-center bg-[#EFF6FF] rounded-[14px] h-[48px] w-[48px] min-h-[48px] min-w-[48px]">
            <TrendingUp height={24} width={24} color="#155DFC" />
          </div>
          <div className="flex flex-col gap-[4px]">
            <Text fontSize={14} fontWeight={600} color="#101828">
              {t("grid_builder_landing_7_title")}
            </Text>
            <Text color="#4A5565" fontSize={12} fontWeight={400}>
              {t("grid_builder_landing_7_description")}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
